import { useEffect, useState } from "react";
import {
  FaClockRotateLeft,
  FaBrain,
  FaCrown,
  FaAtom,
  FaNoteSticky,
  FaCalendarDays,
  FaWandMagicSparkles,
  FaChevronRight,
  FaMicrochip,
  FaFrog,
  FaBug,
} from "react-icons/fa6";
import TopMenu from "../top-menu/TopMenu";
import BottomMenu from "../bottom-menu/BottomMenu";
import {
  getAiAnalysisHistory,
  getAiModels,
  getTopMenuData,
  postAiAnalysis,
} from "../../api/mainApi";
import Premium from "../premium/Premium";
import FullScreenPopup from "../fullScreenPopup/FullScreenPopup";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTelegram } from "../../context/TelegramContext";

function AiAnalysis() {
  const { t } = useTranslation();
  const tg = useTelegram(); // Отримуємо інстанс Telegram

  const [points, setPoints] = useState();
  const [view, setView] = useState("default"); // default, analysis, result
  const [models, setModels] = useState({});
  const [selectedModel, setSelectedModel] = useState(
    localStorage.getItem("selectedAiModel") || "Basic",
  );
  const [analysisPrice, setAnalysisPrice] = useState(
    Number(localStorage.getItem("selectedAiAnalysisPrice")) || 25,
  );
  const [isUserPremium, setIsUserPremium] = useState(null);
  const [showPremium, setShowPremium] = useState(false);
  const [analysisResult, setAnalysisResult] = useState();
  const [analysisHistory, setAnalysisHistory] = useState(
    JSON.parse(localStorage.getItem("analysisHistory")) || null,
  );
  const [modelsLoading, setModelsLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [errorPopup, setErrorPopup] = useState(null);
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  const [catchScore, setCatchScore] = useState(0);
  const [bugPosition, setBugPosition] = useState({ top: "50%", left: "50%" });

  // Отримуємо масив фраз. Якщо переклад ще не завантажився, ставимо порожній масив як fallback
  const translatedPhrases =
    t("analysis.phrases", { returnObjects: true }) || [];
  const phrasesLength = Array.isArray(translatedPhrases)
    ? translatedPhrases.length
    : 1;

  useEffect(() => {
    if (view === "analysis") {
      setCatchScore(0); // Скидаємо рахунок при новому запуску

      // Інтервал для зміни текстів
      const textInterval = setInterval(() => {
        setLoadingPhraseIndex((prev) => (prev + 1) % phrasesLength);
      }, 2500);

      // Функція для стрибка мухи
      const moveBug = () => {
        const top = Math.floor(Math.random() * 80 + 10);
        const left = Math.floor(Math.random() * 80 + 10);
        setBugPosition({ top: `${top}%`, left: `${left}%` });
      };

      const bugInterval = setInterval(moveBug, 1200);

      return () => {
        clearInterval(textInterval);
        clearInterval(bugInterval);
      };
    }
  }, [view, phrasesLength]); // Додали phrasesLength у залежності

  // Обробник кліку по мусі
  const handleCatchBug = (e) => {
    e.stopPropagation();
    setCatchScore((prev) => prev + 1);

    // Одразу переміщуємо муху після кліку, щоб не можна було наклікати двічі
    const top = Math.floor(Math.random() * 80 + 10);
    const left = Math.floor(Math.random() * 80 + 10);
    setBugPosition({ top: `${top}%`, left: `${left}%` });
  };

  // --- TELEGRAM BACK BUTTON ЛОГІКА ---
  useEffect(() => {
    if (!tg || !tg.BackButton) return;

    const handleBackButton = () => {
      setView("default");
    };

    if (view === "result") {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBackButton);

      // Блокуємо глобальний navigate(-1) у TelegramBackButton
      window.isInnerViewOpen = true;
    } else {
      tg.BackButton.hide();
      tg.BackButton.offClick(handleBackButton);

      // Знімаємо блокування
      window.isInnerViewOpen = false;
    }

    return () => {
      tg.BackButton.offClick(handleBackButton);
      window.isInnerViewOpen = false; // Обов'язкове очищення
    };
  }, [view, tg]);

  useEffect(() => {
    const savedHistory = localStorage.getItem("analysisHistory");
    if (savedHistory) setHistoryLoading(false);

    async function fetchModels() {
      const data = await getAiModels();
      if (!data?.aiModelsSnapshot) return;

      const sortedModels = Object.entries(data.aiModelsSnapshot).sort(
        (a, b) => a[1].quality - b[1].quality,
      );

      const sortedModelsObject = Object.fromEntries(sortedModels);
      setModels(sortedModelsObject);

      if (!localStorage.getItem("selectedAiModel")) {
        const defaultModel = sortedModels[0][0];
        const defaultPrice = sortedModels[0][1].analysis_price;

        setSelectedModel(defaultModel);
        setAnalysisPrice(defaultPrice);

        localStorage.setItem("selectedAiModel", defaultModel);
        localStorage.setItem("selectedAiAnalysisPrice", defaultPrice);
      } else {
        const storedModel = localStorage.getItem("selectedAiModel");
        if (storedModel && data.aiModelsSnapshot[storedModel]) {
          const price = data.aiModelsSnapshot[storedModel].analysis_price;
          setAnalysisPrice(price);
          localStorage.setItem("selectedAiAnalysisPrice", price);
        }
      }
      setModelsLoading(false);
    }

    async function fetchHistory() {
      try {
        const data = await getAiAnalysisHistory();
        const historyArray = Array.isArray(data.analysisHistory)
          ? data.analysisHistory
          : Object.values(data.analysisHistory || {});

        const sortedHistory = historyArray.sort(
          (a, b) => b.timestamp - a.timestamp,
        );

        setAnalysisHistory(sortedHistory);
        setHistoryLoading(false);
        localStorage.setItem("analysisHistory", JSON.stringify(sortedHistory));
      } catch (error) {
        console.error("Помилка отримання історії:", error);
        setHistoryLoading(false);
      }
    }

    fetchModels();
    fetchHistory();
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getTopMenuData();
        setPoints(data.points);
        setIsUserPremium(data.premium);
      } catch (error) {
        console.error("Помилка отримання TopMenuData:", error);
      }
    }
    fetchData();
  }, []);

  const handleModelChange = (modelKey, premiumModel) => {
    if ((!isUserPremium && !premiumModel) || isUserPremium) {
      setSelectedModel(modelKey);
      setAnalysisPrice(models[modelKey].analysis_price);
      localStorage.setItem("selectedAiModel", modelKey);
      localStorage.setItem(
        "selectedAiAnalysisPrice",
        models[modelKey].analysis_price,
      );
    } else {
      setShowPremium(true);
    }
  };

  const handleAiAnalysis = async () => {
    if (points < analysisPrice) return setShowPremium(true);

    setView("analysis");

    try {
      const data = await postAiAnalysis({
        model: selectedModel || "Basic",
      });

      setPoints(data.points);
      setAnalysisResult(data);
      setView("result");

      const newEntry = {
        timestamp: data.timestamp,
        text: data.reply,
        moods: data.moods,
        notes: data.notes,
        model: data.model,
      };
      const updatedHistory = [newEntry, ...analysisHistory];
      setAnalysisHistory(updatedHistory);
      localStorage.setItem("analysisHistory", JSON.stringify(updatedHistory));
      setErrorPopup(null);
    } catch (e) {
      const errorKey = e?.response?.data?.error || "something-went-wrong";
      setView("default");
      setErrorPopup(t(`alert.${errorKey}`));
    }
  };

  const handleShowAnalysis = async (data) => {
    await setAnalysisResult(data);
    await setView("result");
  };

  const formatDate = (timestamp) => {
    const d = new Date(timestamp);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  return (
    <div className="h-dvh -mx-4 flex flex-col relative">
      {/* ===== DEFAULT VIEW ===== */}
      {view === "default" && (
        <div className="flex flex-col h-full flex-1 overflow-hidden px-4">
          {errorPopup && (
            <FullScreenPopup
              status="error"
              text={errorPopup}
              buttonText={t("common.close")}
              onClose={() => setErrorPopup(null)}
            />
          )}

          <TopMenu />

          {showPremium && (
            <Premium
              showPremium={showPremium}
              setShowPremium={setShowPremium}
            />
          )}

          <div className="flex-1 flex flex-col gap-4 overflow-y-auto hidden-scrollbar pb-24 pt-2 ">
            {/* Hero Card */}
            <div className="flex flex-col rounded-xl bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move shadow-lg relative group">
              <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                <FaBrain className="w-24 h-24 text-white rotate-12" />
              </div>

              <div className="p-4 relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-white/20 p-2.5 rounded-full shadow-inner">
                    <FaWandMagicSparkles className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 uppercase font-bold tracking-wider mb-0.5">
                      {t("analysis.title")}
                    </p>
                    <p className="text-lg font-black text-white leading-none tracking-wide">
                      {t("analysis.create") || "New Analysis"}
                    </p>
                  </div>
                </div>

                {models && !modelsLoading ? (
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(models).map(([key, model]) => {
                      const isSelected = selectedModel === key;
                      return (
                        <button
                          key={key}
                          onClick={() => handleModelChange(key, model.premium)}
                          className={`
                                        relative flex flex-col items-center justify-center py-2 px-1 rounded-lg transition-all duration-200 border
                                        ${
                                          isSelected
                                            ? "bg-white text-emerald-600 border-white shadow-md transform scale-[1.02]"
                                            : "bg-black/20 text-white/80 border-transparent hover:bg-black/30"
                                        }
                                    `}
                        >
                          {model.premium && (
                            <div className="absolute top-1 right-1">
                              <FaCrown
                                className={`text-[8px] ${isSelected ? "text-yellow-500" : "text-yellow-400"}`}
                              />
                            </div>
                          )}
                          <span className="text-[10px] font-bold uppercase tracking-wider">
                            {key}
                          </span>
                          <span
                            className={`text-sm font-medium ${isSelected ? "opacity-100" : "opacity-70"}`}
                          >
                            {model.analysis_price}{" "}
                            <FaBrain className="inline -mt-px" />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-10 bg-white/20 rounded-lg animate-pulse"
                      ></div>
                    ))}
                  </div>
                )}
              </div>

              <button
                disabled={modelsLoading}
                onClick={handleAiAnalysis}
                className="bg-black/10 hover:bg-black/20 p-3 text-center text-sm font-bold uppercase tracking-wide transition-colors flex items-center justify-center gap-2 border-t border-white/10 w-full"
              >
                <span>{t("analysis.button")}</span>
                <FaBrain className="text-xs opacity-70" />
              </button>
            </div>

            {/* --- History Section --- */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 px-1 opacity-80">
                <FaClockRotateLeft className="text-slate-400" />
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">
                  {t("analysis.historyTitle")}
                </h3>
              </div>

              <div className="flex flex-col gap-3">
                {historyLoading ? (
                  [1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-24 bg-slate-800 rounded-2xl animate-pulse"
                    />
                  ))
                ) : analysisHistory?.length > 0 ? (
                  analysisHistory.map((analysis) => (
                    <div
                      key={analysis.id || analysis.timestamp}
                      onClick={() => handleShowAnalysis(analysis)}
                      className="group bg-slate-800/60 border border-white/5 p-4 rounded-2xl active:bg-slate-800 transition-colors cursor-pointer relative overflow-hidden text-start"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex flex-col">
                          <span className="text-white font-bold text-sm">
                            {formatDate(analysis.timestamp)}
                          </span>
                        </div>
                        <div className="px-2 py-1 rounded-lg bg-slate-900 border border-slate-700 flex items-center gap-1.5">
                          <FaAtom
                            className={`text-[10px] ${analysis.model !== "Basic" ? "text-purple-400" : "text-slate-400"}`}
                          />
                          <span className="text-[10px] font-bold text-slate-300 uppercase">
                            {analysis?.model ?? "Basic"}
                          </span>
                        </div>
                      </div>

                      <p className="text-slate-300 text-sm line-clamp-2 leading-relaxed mb-3 opacity-90 text-start">
                        {analysis.text}
                      </p>

                      <div className="flex items-center justify-between border-t border-white/5 pt-3">
                        <div className="flex gap-2">
                          <div className="flex items-center gap-1.5 text-xs text-emerald-400 font-medium bg-emerald-500/10 px-2 py-0.5 rounded-md">
                            <FaCalendarDays />
                            <span>{analysis?.moods ?? 0}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-cyan-400 font-medium bg-cyan-500/10 px-2 py-0.5 rounded-md">
                            <FaNoteSticky />
                            <span>{analysis?.notes ?? 0}</span>
                          </div>
                        </div>
                        <div className="text-slate-500 group-hover:text-emerald-400 transition-colors">
                          <FaChevronRight className="text-xs" />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-10 opacity-50">
                    <FaBrain className="w-12 h-12 mx-auto mb-2 text-slate-600" />
                    <p className="text-sm text-slate-400">
                      {t("analysis.emptyHistory")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <BottomMenu />
        </div>
      )}

      {/* ===== НОВИЙ LOADING VIEW ===== */}

      {view === "analysis" && (
        <div className="flex h-full w-full absolute inset-0 z-50 flex-col justify-center items-center bg-gradient-to-br from-emerald-800 via-teal-900 to-slate-900 overflow-hidden">
          {/* Міні-гра на фоні: Муха */}
          <div
            onClick={handleCatchBug}
            className="absolute z-20 p-6 cursor-pointer transition-all duration-300 ease-in-out hover:scale-110 active:scale-90 tap-highlight-transparent"
            style={{
              top: bugPosition.top,
              left: bugPosition.left,
              transform: "translate(-50%, -50%)",
            }}
          >
            <FaBug className="w-8 h-8 text-emerald-300 drop-shadow-[0_0_12px_rgba(110,231,183,1)] animate-pulse" />
          </div>

          {/* Центральна інформаційна картка */}
          <div className="relative z-10 flex flex-col items-center max-w-xs w-full px-6 bg-slate-950/40 py-8 rounded-3xl backdrop-blur-md border border-emerald-500/20 shadow-2xl pointer-events-none">
            {/* Лічильник впійманих мух */}
            <div className="absolute -top-4 bg-slate-900 border border-emerald-500/50 px-4 py-1.5 rounded-full shadow-lg">
              <span className="text-emerald-400 font-black text-sm uppercase tracking-wider">
                {t("analysis.catchScore", { count: catchScore })}
              </span>
            </div>

            {/* Пульсуюча ШІ-Жаба */}
            <div className="relative w-24 h-24 mb-6 flex items-center justify-center">
              <div className="absolute inset-0 border-4 border-emerald-400/40 rounded-full animate-[spin_3s_linear_infinite]"></div>
              <div className="absolute inset-0 bg-emerald-500 blur-xl opacity-30 animate-pulse rounded-full"></div>
              <FaFrog className="w-12 h-12 text-emerald-400 animate-bounce relative z-10 drop-shadow-[0_0_15px_rgba(52,211,153,0.8)]" />
            </div>

            <h3 className="text-xl font-black text-white tracking-widest uppercase mb-4 text-center drop-shadow-md">
              {t("analysis.loadingTitle")}
            </h3>

            {/* Sims-подібний текст з перекладів */}
            <div className="h-12 flex items-center justify-center text-center px-2">
              <p className="text-sm text-emerald-200 font-medium transition-opacity duration-300 animate-fade-in drop-shadow-sm">
                {translatedPhrases[loadingPhraseIndex] ||
                  t("analysis.loadingTitle")}
              </p>
            </div>

            {/* Бар завантаження */}
            <div className="w-full h-1.5 bg-slate-800/80 rounded-full overflow-hidden mt-4 relative">
              <div
                className="absolute top-0 left-0 h-full w-1/2 bg-gradient-to-r from-emerald-600 to-emerald-300 rounded-full animate-[shimmer_1.5s_infinite] -translate-x-full"
                style={{
                  animationName: "scanLine",
                  animationDuration: "1.5s",
                  animationIterationCount: "infinite",
                  animationTimingFunction: "linear",
                }}
              ></div>
            </div>

            <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-4 font-bold animate-pulse">
              {t("analysis.waiting")}
            </p>
          </div>

          <style
            dangerouslySetInnerHTML={{
              __html: `
              @keyframes scanLine {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(200%); }
              }
              @keyframes fade-in {
                0% { opacity: 0; transform: translateY(5px); }
                20% { opacity: 1; transform: translateY(0); }
                80% { opacity: 1; transform: translateY(0); }
                100% { opacity: 0; transform: translateY(-5px); }
              }
              .animate-fade-in {
                animation: fade-in 2.5s infinite;
              }
              .tap-highlight-transparent {
                -webkit-tap-highlight-color: transparent;
              }
            `,
            }}
          />
        </div>
      )}

      {/* ===== RESULT VIEW ===== */}
      {view === "result" && analysisResult && (
        <div className="flex flex-col h-full bg-slate-950 absolute inset-0 z-50 overflow-hidden">
          {/* Header - Кнопку "Назад" прибрано, бо тепер працює нативна від Telegram */}
          <div className="flex justify-center items-center w-full px-4 pt-11 pb-2 bg-slate-900/80 backdrop-blur-md border-b border-white/5 absolute top-0 z-20">
            <p className="text-lg font-bold text-white tracking-wide">
              {t("analysis.resultTitle")}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto pt-28 pb-10 px-5 hidden-scrollbar text-start">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 mb-6 flex flex-col gap-3 shadow-lg">
              <div className="flex justify-between items-center border-b border-white/5 pb-3">
                <span className="text-slate-400 text-xs uppercase font-bold tracking-wider">
                  {t("analysis.created") || "Created"}
                </span>
                <span className="text-white font-bold text-sm">
                  {formatDate(analysisResult.timestamp)}
                </span>
              </div>

              <div className="flex gap-1">
                <div className="flex-1 bg-slate-900 rounded-xl p-2 flex flex-col items-center gap-1 text-center border border-white/5">
                  <FaCalendarDays className="text-emerald-400 text-sm" />
                  <span className="text-[10px] text-slate-400 uppercase font-bold">
                    {t("marks") || "Moods"}
                  </span>
                  <span className="text-lg font-bold text-white leading-none">
                    {analysisResult?.moods ?? 0}
                  </span>
                </div>
                <div className="flex-1 bg-slate-900 rounded-xl p-2 flex flex-col items-center gap-1 border border-white/5">
                  <FaNoteSticky className="text-cyan-400 text-sm" />
                  <span className="text-[10px] text-slate-400 uppercase font-bold">
                    {t("notes")}
                  </span>
                  <span className="text-lg font-bold text-white leading-none">
                    {analysisResult?.notes ?? 0}
                  </span>
                </div>
                <div className="flex-1 bg-slate-900 rounded-xl p-2 flex flex-col items-center gap-1 border border-white/5">
                  <FaAtom className="text-purple-400 text-sm" />
                  <span className="text-[10px] text-slate-400 uppercase font-bold">
                    {t("model")}
                  </span>
                  <span className="text-lg font-bold text-white leading-none mt-1">
                    {analysisResult?.model ?? "Basic"}
                  </span>
                </div>
              </div>
            </div>

            {/* ВИВІД MARKDOWN ДЛЯ ШІ */}
            <div className="text-base leading-relaxed pb-10 text-start">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Стилізуємо заголовки
                  h1: ({ node, ...props }) => (
                    <h1
                      className="text-2xl font-black text-emerald-400 mt-6 mb-4"
                      {...props}
                    />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2
                      className="text-xl font-bold text-cyan-400 mt-5 mb-3"
                      {...props}
                    />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3
                      className="text-lg font-bold text-white mt-4 mb-2"
                      {...props}
                    />
                  ),
                  // Стилізуємо звичайний текст і списки
                  p: ({ node, ...props }) => (
                    <p className="text-slate-300 mb-2" {...props} />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul
                      className="list-disc list-inside text-slate-300 mb-4 space-y-1 marker:text-emerald-500"
                      {...props}
                    />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol
                      className="list-decimal list-inside text-slate-300 mb-4 space-y-1 marker:text-cyan-500"
                      {...props}
                    />
                  ),
                  li: ({ node, ...props }) => (
                    <li className="pl-1" {...props} />
                  ),
                  strong: ({ node, ...props }) => (
                    <strong className="font-bold text-white" {...props} />
                  ),
                  // Стилізуємо РОЗДІЛЬНИКИ (---)
                  hr: ({ node, ...props }) => (
                    <hr
                      className="my-6 border-slate-700/60 border-t-2 rounded-full"
                      {...props}
                    />
                  ),
                  // Стилізуємо ТАБЛИЦІ
                  table: ({ node, ...props }) => (
                    <div className="overflow-x-auto my-6 border border-slate-700/50 rounded-xl bg-slate-900/50">
                      <table
                        className="min-w-full divide-y divide-slate-700/50 text-sm"
                        {...props}
                      />
                    </div>
                  ),
                  thead: ({ node, ...props }) => (
                    <thead className="bg-slate-800/80" {...props} />
                  ),
                  th: ({ node, ...props }) => (
                    <th
                      className="px-4 py-3 text-left font-bold text-emerald-400 uppercase tracking-wider"
                      {...props}
                    />
                  ),
                  tbody: ({ node, ...props }) => (
                    <tbody className="divide-y divide-slate-800" {...props} />
                  ),
                  td: ({ node, ...props }) => (
                    <td
                      className="px-4 py-3 text-slate-300 whitespace-nowrap"
                      {...props}
                    />
                  ),
                  // Стилізуємо цитати, якщо ШІ щось виділить
                  blockquote: ({ node, ...props }) => (
                    <blockquote
                      className="border-l-4 border-emerald-500 pl-4 py-1 my-4 bg-emerald-500/10 rounded-r-lg text-slate-300 italic"
                      {...props}
                    />
                  ),
                }}
              >
                {analysisResult.reply ?? analysisResult.text}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AiAnalysis;
